import React, { useState, useEffect, useCallback, useRef } from 'react';
import useAuth from "../../Auth/useAuth";
import { io } from 'socket.io-client';
import axios from 'axios';
import config from '../../config';
import './ChatNotification.css';
import { useNavigate, useLocation } from 'react-router-dom';

axios.defaults.baseURL = config.BASE_URL;

const ChatNotification = () => {
  const { activeModule, setActiveModule, username, selectedChat, setSelectedChat } = useAuth();
  const [notification, setNotification] = useState(null);
  const [socket, setSocket] = useState(null);
  const [userChats, setUserChats] = useState([]);
  const [isLoadingChats, setIsLoadingChats] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const socketRef = useRef(null);
  const notificationTimeoutRef = useRef(null);

  // Fetch chats only once when component mounts
  useEffect(() => {
    let isMounted = true;
    
    const fetchChats = async () => {
      if (isLoadingChats) return; // Prevent multiple simultaneous calls
      
      try {
        setIsLoadingChats(true);
        console.log('Fetching chats...');
        
        const response = await axios.get('/api/chats', {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
          }
        });

        if (isMounted) {
          console.log('Chats fetched:', response.data);
          setUserChats(response.data);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching chats:', error);
          // Handle 401 errors gracefully
          if (error.response?.status === 401) {
            console.warn('Chat access unauthorized - user may not have chat permissions');
          }
        }
      } finally {
        if (isMounted) {
          setIsLoadingChats(false);
        }
      }
    };

    fetchChats();

    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency array - fetch only once

  // Socket connection setup
  useEffect(() => {
    if (!username) return;

    console.log('Initializing socket connection...');

    // Close any existing socket first
    if (socketRef.current) {
      socketRef.current.disconnect();
    }

    const newSocket = io(config.BASE_URL || "https://saleseco.mindfuturetechsupport.com", {
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      autoConnect: true,
      transports: ['websocket', 'polling']
    });

    newSocket.on('connect', () => {
      console.log('Socket connected with ID:', newSocket.id);
      newSocket.emit('register', { userId: username });
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
    });

    newSocket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
    });

    socketRef.current = newSocket;
    setSocket(newSocket);

    return () => {
      console.log('Cleaning up socket connection');
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [username]); // Only reconnect when username changes

  // Handle notifications
  useEffect(() => {
    if (!socket) return;

    const handleNotification = (notificationData) => {
      console.log("Notification data:", notificationData);
      
      // Play notification sound
      try {
        const audio = new Audio('/sounds/notification.mp3');
        audio.play().catch((error) => {
          console.error("Error playing notification sound:", error);
        });
      } catch (error) {
        console.error("Error creating audio:", error);
      }

      setNotification(notificationData);

      // Clear any existing timeout
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }

      // Set new timeout to clear notification
      notificationTimeoutRef.current = setTimeout(() => {
        setNotification(null);
        notificationTimeoutRef.current = null;
      }, 5000);
    };

    socket.on('notification', handleNotification);

    return () => {
      socket.off('notification', handleNotification);
      // Clear timeout on cleanup
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
        notificationTimeoutRef.current = null;
      }
    };
  }, [socket]);

  const handleNotificationClick = useCallback((e) => {
    e.preventDefault();
    
    if (!notification) return;

    // Clear active module
    sessionStorage.setItem('actMod', '');
    setActiveModule('');

    // Find the current chat
    const currentChat = userChats.find((chat) => {
      return chat.participants.includes(notification.senderId) && 
             chat.participants.includes(notification.receiverId);
    });

    // Clear notification
    setNotification(null);
    
    // Clear timeout
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
      notificationTimeoutRef.current = null;
    }

    // Navigate to chat with state
    navigate('/chat', {
      state: {
        currentChat: currentChat
      }
    });
  }, [notification, userChats, setActiveModule, navigate]);

  const handleCloseNotification = useCallback((e) => {
    e.stopPropagation();
    setNotification(null);
    
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
      notificationTimeoutRef.current = null;
    }
  }, []);

  // Don't render if no notification
  if (!notification) return null;

  return (
    <div className="chat-Notification" onClick={handleNotificationClick}>
      <div className="chat-avatar">
        {notification?.senderId ? notification.senderId.charAt(0).toUpperCase() : ""}
      </div>
      <div className="chat-content">
        <div className="chat-sender">{notification.senderId}</div>
        {notification.file ? (
          <div className="chat-message">
            <div className='chatFile'>
              {`📎${(notification?.file?.fileName)?.split('-')?.slice(1)?.join('-')}`}
            </div>
            <div className='chatText'>{notification.content}</div>
          </div>
        ) : (
          <div className="chatText">
            {notification.content}
          </div>
        )}
      </div>
      <button className="chat-close" onClick={handleCloseNotification}>
        &times;
      </button>
    </div>
  );
};

export default ChatNotification;